import type { ReactNode } from 'react';

export interface FieldShellProps {
  id: string;
  label: string;
  required?: boolean;
  hint?: string;
  hintId: string;
  error?: string;
  errorId: string;
  children: ReactNode;
}

/**
 * Casca partilhada por todos os campos: rótulo, ajuda e erro.
 *
 * O erro é TEXTO com ícone e palavra ("Erro: …"), ligado ao input por
 * `aria-describedby`. Uma borda vermelha sozinha não é uma mensagem — ela pode
 * não ver a diferença e um leitor de tela não a lê de todo.
 */
export function FieldShell({
  id,
  label,
  required = false,
  hint,
  hintId,
  error,
  errorId,
  children,
}: FieldShellProps) {
  return (
    <div className="flos-field">
      <label className="flos-field__label" htmlFor={id}>
        {label}
        {required ? (
          <>
            {' '}
            <span className="flos-field__required" aria-hidden="true">
              *
            </span>
            <span className="flos-sr-only">(obrigatório)</span>
          </>
        ) : null}
      </label>
      {children}
      {hint ? (
        <span className="flos-field__hint" id={hintId}>
          {hint}
        </span>
      ) : null}
      {error ? (
        <span className="flos-field__error" id={errorId} role="alert">
          <span className="flos-icon" aria-hidden="true">
            ⛔
          </span>
          <span>
            <span className="flos-sr-only">Erro: </span>
            {error}
          </span>
        </span>
      ) : null}
    </div>
  );
}

/** Constrói o `aria-describedby` só com os ids que existem mesmo. */
export function describedBy(
  hint: string | undefined,
  hintId: string,
  error: string | undefined,
  errorId: string,
): string | undefined {
  const ids: string[] = [];
  if (hint) ids.push(hintId);
  if (error) ids.push(errorId);
  return ids.length ? ids.join(' ') : undefined;
}
